import * as React from "react";

import { normalizeNumberEntry, toLatinDigits } from "@/lib/format";
import { cn } from "@/lib/utils";

type InputProps = React.ComponentProps<"input"> & { normalizeDigits?: boolean };

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, inputMode, onChange, normalizeDigits, step, ...props }, ref) => {
    const numberMode = inputMode === "numeric" || (type === "number" && Number(step) === 1)
      ? "numeric"
      : inputMode === "decimal" || type === "number"
        ? "decimal"
        : null;
    const normalize = numberMode
      ? (value: string) => normalizeNumberEntry(value, numberMode === "decimal")
      : type === "tel" || inputMode === "tel" || normalizeDigits
        ? toLatinDigits
        : null;

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
      if (normalize) {
        const input = event.currentTarget;
        const original = input.value;
        const normalized = normalize(original);
        if (normalized !== original) {
          const start = input.selectionStart;
          const end = input.selectionEnd;
          input.value = normalized;
          if (start !== null && end !== null) {
            input.setSelectionRange(
              normalize(original.slice(0, start)).length,
              normalize(original.slice(0, end)).length,
            );
          }
        }
      }
      onChange?.(event);
    }

    return (
      <input
        type={type === "number" ? "text" : type}
        inputMode={inputMode ?? (type === "number" ? numberMode ?? undefined : undefined)}
        step={step}
        className={cn(
          "flex h-12 w-full min-w-0 rounded-xl border border-input bg-card px-3 py-2 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-invalid:border-destructive disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className,
        )}
        ref={ref}
        onChange={handleChange}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
